import { apiGet } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Foundry → Deployments (read-only). GET api/Foundry/Deployments returns every
// deployment on the Azure AI Foundry project, compared with the four platform
// registries that name a deployment: the platform default (ai.AIApi), merchant
// defaults (ai.MerchantAIApi), agent models (ai.AIModel) and the referral
// document reader (intake.MerchantDocumentReaderSetting).
//
// Every reading, label and tone is composed SERVER-SIDE in
// OO.Domain.AIInfo.Foundry.FoundryDeploymentRules. The page renders them.
// ─────────────────────────────────────────────────────────────────────────────

export type FoundryStatusKey = 'missing-named' | 'in-use' | 'switched-off' | 'unused' | 'missing-stale';

export type FoundryTone = 'error' | 'success' | 'warning' | 'default';

export interface FoundryUse {
  kindLabel: string;
  rowId: number;
  merchantId: number | null;
  isOn: boolean;
  reading: string;
}

export interface FoundryDeploymentRow {
  name: string;
  existsInFoundry: boolean;
  modelName: string | null;
  modelVersion: string | null;
  publisher: string | null;
  skuName: string | null;
  capacity: number | null;
  capabilities: string[];
  statusKey: FoundryStatusKey;
  statusLabel: string;
  tone: FoundryTone;
  reading: string;
  uses: FoundryUse[];
}

export interface FoundryDeploymentCounts {
  deployed: number;
  inUse: number;
  switchedOff: number;
  unused: number;
  missingAndNamed: number;
  missingStale: number;
}

export interface FoundryDeploymentsOverview {
  apiId: number;
  projectHost: string | null;
  projectName: string | null;
  generatedAtUtc: string;
  counts: FoundryDeploymentCounts;
  /** Most urgent first; the server orders them. */
  rows: FoundryDeploymentRow[];
}

export async function getFoundryDeployments(): Promise<FoundryDeploymentsOverview> {
  const wrapped = await apiGet<OperationResult<FoundryDeploymentsOverview>>('/api/Foundry/Deployments');
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load Foundry deployments');
  return wrapped.data;
}
